// src/components/communs/FormationJson.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    X, Clock, ChevronLeft, ChevronRight, Maximize2, Minimize2,
    BookOpen, FileText, GraduationCap, AlertCircle, Award, FileQuestion,
    Home, CheckCircle, Layers, LogOut
} from 'lucide-react';

export default function FormationJson({
    module,
    jsonContent,
    formationTitre,
    moduleOrdre,
    totalModules,
    onClose,
    isOpen,
    onModuleChange,
    onComplete,
    onError,
    mode = 'production'
}) {
    // ============================================================
    // 1. Chargement de la progression depuis localStorage
    // ============================================================
    const loadProgress = useCallback((moduleId) => {
        if (!moduleId) return { page: 1, time: 0, progress: 0, visitedPages: [] };
        try {
            const saved = localStorage.getItem(`module_progress_${moduleId}`);
            if (saved) {
                const data = JSON.parse(saved);
                return {
                    page: data.page || 1,
                    time: data.timeSpent || 0,
                    progress: data.progress || 0,
                    visitedPages: data.visitedPages || []
                };
            }
        } catch (e) {
            console.error('Erreur chargement progression:', e);
        }
        return { page: 1, time: 0, progress: 0, visitedPages: [] };
    }, []);

    // ============================================================
    // 2. États
    // ============================================================
    const [currentModuleId, setCurrentModuleId] = useState(module?.id);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [timeSpent, setTimeSpent] = useState(0);
    const [visitedPages, setVisitedPages] = useState([]);
    const [isPaused, setIsPaused] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [evaluationResults, setEvaluationResults] = useState({});

    const containerRef = useRef(null);
    const timerRef = useRef(null);
    const saveIntervalRef = useRef(null);
    const isMountedRef = useRef(true);
    const pageMarkedRef = useRef({});

    // ============================================================
    // 3. Parsing du contenu JSON
    // ============================================================
    const parsedContent = useMemo(() => {
        if (!jsonContent) return null;
        try {
            return typeof jsonContent === 'string' ? JSON.parse(jsonContent) : jsonContent;
        } catch (e) {
            console.error("Erreur parsing JSON:", e);
            if (onError) onError(e);
            return { error: "Format JSON invalide", details: e.message };
        }
    }, [jsonContent, onError]);

    // ============================================================
    // 4. Construction des pages de navigation
    // ============================================================
    const navigationPages = useMemo(() => {
        if (!parsedContent || parsedContent.error) return [];

        const pages = [];

        pages.push({
            type: 'ACCUEIL_FORMATION',
            id: 'accueil',
            label: 'Accueil',
            data: parsedContent,
            moduleId: null
        });

        if (parsedContent.modules && Array.isArray(parsedContent.modules)) {
            parsedContent.modules.forEach((mod, modIndex) => {
                const moduleId = mod.id || `mod-${modIndex + 1}`;

                pages.push({
                    type: 'ANNOUNCE_MODULE',
                    id: `${moduleId}-announce`,
                    label: `Module ${modIndex + 1}: ${mod.titre}`,
                    data: mod,
                    moduleId: moduleId
                });

                if (mod.lecons && Array.isArray(mod.lecons)) {
                    mod.lecons.forEach((lecon, lecIndex) => {
                        pages.push({
                            type: 'LECON_TITRE',
                            id: `lec-${moduleId}-${lecIndex + 1}-titre`,
                            label: lecon.titre || `Leçon ${lecIndex + 1}`,
                            data: lecon,
                            moduleId: moduleId
                        });

                        if (lecon.contenu) {
                            pages.push({
                                type: 'CONTENU_LECON',
                                id: `lec-${moduleId}-${lecIndex + 1}-contenu`,
                                label: `Contenu: ${lecon.titre || `Leçon ${lecIndex + 1}`}`,
                                data: lecon,
                                moduleId: moduleId
                            });
                        }

                        if (lecon.resumeLecon || lecon.resume) {
                            pages.push({
                                type: 'RESUME_LECON',
                                id: `lec-${moduleId}-${lecIndex + 1}-resume`,
                                label: `Résumé: ${lecon.titre || `Leçon ${lecIndex + 1}`}`,
                                data: lecon,
                                moduleId: moduleId
                            });
                        }
                    });
                }

                if (mod.evaluations && Array.isArray(mod.evaluations)) {
                    mod.evaluations.forEach((evalItem, evalIndex) => {
                        pages.push({
                            type: 'EVALUATION',
                            id: `eval-${moduleId}-${evalIndex + 1}`,
                            label: evalItem.titre || `Évaluation ${evalIndex + 1}`,
                            data: evalItem,
                            evalIndex: evalIndex,
                            moduleId: moduleId
                        });
                    });
                }

                pages.push({
                    type: 'MODULE_END',
                    id: `${moduleId}-end`,
                    label: `Fin du module ${modIndex + 1}`,
                    data: mod,
                    moduleId: moduleId
                });
            });
        }

        pages.push({
            type: 'FIN_FORMATION',
            id: 'fin-formation',
            label: 'Fin de la formation',
            data: parsedContent.FinFormation || {
                titreResume: 'Félicitations !',
                aRetenir: 'Vous avez terminé la formation.'
            },
            moduleId: null
        });

        return pages;
    }, [parsedContent]);

    // ============================================================
    // 5. getPageId
    // ============================================================
    const getPageId = useCallback((index) => {
        return navigationPages[index]?.id || null;
    }, [navigationPages]);

    // ============================================================
    // 6. Effet : changement de module + restauration de progression
    // ============================================================
    useEffect(() => {
        if (!module?.id) return;
        if (module.id === currentModuleId) return;

        const progressData = loadProgress(module.id);
        const newPageIndex = Math.max(0, progressData.page - 1);

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentModuleId(module.id);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCurrentPageIndex(newPageIndex);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setTimeSpent(progressData.time);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVisitedPages(progressData.visitedPages || []);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setEvaluationResults({});

        pageMarkedRef.current = {};

        const pageId = getPageId(newPageIndex);
        if (pageId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setVisitedPages(prev => {
                if (prev.includes(pageId)) return prev;
                return [...prev, pageId];
            });
            pageMarkedRef.current[pageId] = true;
        }
    }, [module, currentModuleId, loadProgress, getPageId]);

    // ============================================================
    // 7. Effet : marquer la page courante comme visitée
    // ============================================================
    useEffect(() => {
        if (!module?.id) return;
        if (navigationPages.length === 0) return;
        const pageId = getPageId(currentPageIndex);
        if (!pageId) return;
        if (pageMarkedRef.current[pageId]) return;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setVisitedPages(prev => {
            if (prev.includes(pageId)) return prev;
            return [...prev, pageId];
        });
        pageMarkedRef.current[pageId] = true;
    }, [currentPageIndex, navigationPages, getPageId, module]);

    // ============================================================
    // 8. Fonctions de navigation
    // ============================================================
    const goToPreviousPage = () => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(prev => prev - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPageIndex < navigationPages.length - 1) {
            setCurrentPageIndex(prev => prev + 1);
        }
    };

    const goToHome = () => {
        if (currentPageIndex !== 0) {
            setCurrentPageIndex(0);
        }
    };

    // ============================================================
    // 9. Module ranges et progression
    // ============================================================
    const moduleRanges = useMemo(() => {
        const ranges = {};
        let currentModuleId = null;
        let startIndex = -1;

        navigationPages.forEach((page, index) => {
            if (page.type === 'ANNOUNCE_MODULE') {
                currentModuleId = page.moduleId;
                startIndex = index;
            } else if (page.type === 'MODULE_END' && page.moduleId === currentModuleId) {
                if (currentModuleId) {
                    ranges[currentModuleId] = { start: startIndex, end: index };
                }
                currentModuleId = null;
                startIndex = -1;
            }
        });
        return ranges;
    }, [navigationPages]);

    const currentModuleRange = moduleRanges[module?.id];
    const isCurrentModuleComplete = useMemo(() => {
        if (!currentModuleRange) return false;
        const { start, end } = currentModuleRange;
        for (let i = start; i <= end; i++) {
            const page = navigationPages[i];
            if (!visitedPages.includes(page.id)) {
                return false;
            }
        }
        return true;
    }, [currentModuleRange, navigationPages, visitedPages]);

    // ============================================================
    // 10. Sauvegarde de la progression
    // ============================================================
    const saveProgressToStorage = useCallback(() => {
        if (mode === 'demo') return;
        if (!module?.id || !isMountedRef.current) return;
        const totalPages = navigationPages.length;
        const uniqueVisited = visitedPages.length;
        const progressValue = totalPages > 0 ? Math.min(100, Math.round((uniqueVisited / totalPages) * 100)) : 0;
        const data = {
            moduleId: module.id,
            page: currentPageIndex + 1,
            timeSpent: timeSpent,
            progress: progressValue,
            visitedPages: visitedPages
        };
        localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(data));
    }, [mode, module, currentPageIndex, timeSpent, visitedPages, navigationPages.length]);

    // Sauvegarde périodique
    useEffect(() => {
        if (isOpen && module?.id && navigationPages.length > 0 && isMountedRef.current) {
            if (saveIntervalRef.current) clearInterval(saveIntervalRef.current);
            saveIntervalRef.current = setInterval(() => {
                saveProgressToStorage();
            }, 30000);
            return () => {
                if (saveIntervalRef.current) {
                    clearInterval(saveIntervalRef.current);
                    saveIntervalRef.current = null;
                }
            };
        }
    }, [isOpen, module?.id, navigationPages.length, saveProgressToStorage]);

    // Sauvegarde différée après changement de page
    useEffect(() => {
        if (navigationPages.length === 0) return;
        const timeout = setTimeout(() => saveProgressToStorage(), 300);
        return () => clearTimeout(timeout);
    }, [currentPageIndex, saveProgressToStorage, navigationPages.length]);

    // Chronomètre
    useEffect(() => {
        if (isOpen && !isPaused && isMountedRef.current) {
            timerRef.current = setInterval(() => {
                setTimeSpent(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isOpen, isPaused]);

    // ============================================================
    // 11. Utilitaires et gestionnaires
    // ============================================================
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleModuleChange = (direction) => {
        if (onModuleChange && isMountedRef.current) {
            saveProgressToStorage();
            if (onComplete && direction > 0) onComplete(module?.id);
            onModuleChange(direction);
        }
    };

    const handleManualSave = useCallback(() => {
        saveProgressToStorage();
    }, [saveProgressToStorage]);

    const handleTerminer = () => {
        if (mode === 'demo') {
            if (onClose) onClose();
            return;
        }
        if (module?.id) {
            const existing = localStorage.getItem(`module_progress_${module.id}`);
            let progressData = {};
            if (existing) {
                try { progressData = JSON.parse(existing); } catch (e) {console.error(e);}
            }
            progressData.completed = true;
            progressData.completedAt = new Date().toISOString();
            localStorage.setItem(`module_progress_${module.id}`, JSON.stringify(progressData));
        }
        if (onComplete) onComplete(module?.id);
        if (onClose) onClose();
    };

    // ============================================================
    // 12. Rendu des pages
    // ============================================================
    const renderPage = (page) => {
        const isVisited = visitedPages.includes(page.id);
        switch (page.type) {
            case 'ACCUEIL_FORMATION':
                return (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] sm:min-h-[400px] p-4 sm:p-8 border border-gray-300 rounded-lg bg-white">
                        <h1 className="text-xl sm:text-4xl font-bold text-center font-serif mb-2 sm:mb-4">
                            {page.data.titre || 'Formation'}
                        </h1>
                        <p className="text-sm sm:text-xl text-gray-700 text-center max-w-3xl font-serif mb-3 sm:mb-6">
                            {page.data.description || ''}
                        </p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-3 justify-center">
                            {page.data.categorie && (
                                <span className="bg-gray-200 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-sm text-gray-700 font-serif">
                                    📚 {page.data.categorie}
                                </span>
                            )}
                            {page.data.niveau && (
                                <span className="bg-gray-200 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-sm text-gray-700 font-serif">
                                    📊 {page.data.niveau}
                                </span>
                            )}
                            {page.data.dureeEstimee && (
                                <span className="bg-gray-200 px-2 py-1 sm:px-4 sm:py-2 rounded-full text-[10px] sm:text-sm text-gray-700 font-serif">
                                    ⏱️ {page.data.dureeEstimee}
                                </span>
                            )}
                        </div>
                        {isVisited && (
                            <div className="mt-3 sm:mt-4 text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="text-[10px] sm:text-sm font-serif">Déjà visitée</span>
                            </div>
                        )}
                    </div>
                );

            case 'ANNOUNCE_MODULE':
                return (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] sm:min-h-[400px] p-4 sm:p-8 border border-gray-300 rounded-lg bg-white">
                        <GraduationCap className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mb-3 sm:mb-6" />
                        <h1 className="text-xl sm:text-3xl font-bold text-center font-serif mb-2 sm:mb-4">
                            {page.data.titre}
                        </h1>
                        <p className="text-sm sm:text-lg text-gray-600 text-center max-w-2xl font-serif">
                            {page.data.description}
                        </p>
                        {isVisited && (
                            <div className="mt-3 sm:mt-4 text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="text-[10px] sm:text-sm font-serif">Déjà visitée</span>
                            </div>
                        )}
                    </div>
                );

            case 'LECON_TITRE':
                return (
                    <div className="flex flex-col items-center justify-center h-full min-h-[300px] sm:min-h-[400px] p-4 sm:p-8 border border-gray-300 rounded-lg bg-white">
                        <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500 mb-2 sm:mb-4" />
                        <h1 className="text-xl sm:text-3xl font-bold text-center font-serif mb-2 sm:mb-3">
                            {page.data.titre}
                        </h1>
                        {page.data.description && (
                            <p className="text-sm sm:text-lg text-gray-600 text-center max-w-2xl font-serif">
                                {page.data.description}
                            </p>
                        )}
                        {isVisited && (
                            <div className="mt-3 sm:mt-4 text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="text-[10px] sm:text-sm font-serif">Déjà visitée</span>
                            </div>
                        )}
                    </div>
                );

            case 'CONTENU_LECON':
                return (
                    <div className="flex flex-col w-full h-full bg-white rounded-lg overflow-hidden shadow-sm">
                        <div className="bg-green-900 text-white px-3 sm:px-6 py-2 sm:py-4 flex justify-between items-center flex-shrink-0">
                            <h2 className="text-sm sm:text-xl text-white font-bold font-serif">
                                {page.data.titre || 'Contenu'}
                            </h2>
                            {isVisited && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />}
                        </div>
                        <div className="flex-1 p-3 sm:p-6 overflow-y-auto text-black">
                            <div className="prose prose-sm sm:prose-lg max-w-none prose-invert font-serif text-black">
                                {page.data.contenu?.corpsTexte && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: page.data.contenu.corpsTexte
                                        }}
                                    />
                                )}
                                {!page.data.contenu?.corpsTexte && (
                                    <p className="text-gray-300">Aucun contenu disponible</p>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 'RESUME_LECON': {
                const resumeContent = page.data.resumeLecon?.aRetenir || page.data.resume || 'Aucun résumé disponible';
                return (
                    <div className="flex flex-col w-full h-full bg-white rounded-lg overflow-hidden shadow-sm">
                        <div className="bg-white text-black px-3 sm:px-6 py-2 sm:py-4 flex justify-between items-center flex-shrink-0">
                            <h2 className="text-sm sm:text-xl font-bold font-serif">
                                Résumé de la leçon
                            </h2>
                            {isVisited && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-300" />}
                        </div>
                        <div className="flex-1 p-4 sm:p-6 overflow-y-auto bg-white flex items-center justify-center">
                            <p className="text-sm sm:text-lg text-gray-700 font-serif text-center max-w-2xl">
                                {resumeContent}
                            </p>
                        </div>
                    </div>
                );
            }

            case 'EVALUATION': {
                const evalIndex = page.evalIndex || 0;
                const evalResult = evaluationResults[evalIndex];
                const isSubmitted = !!evalResult;

                return (
                    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
                        <div className="bg-gray-700 text-white px-3 sm:px-6 py-2 sm:py-4 flex justify-between items-center">
                            <div className="flex items-center gap-1.5 sm:gap-3">
                                <FileQuestion className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
                                <h2 className="text-sm sm:text-xl font-bold font-serif text-white">
                                    {page.data.titre || `Évaluation ${evalIndex + 1}`}
                                </h2>
                            </div>
                            <span className="text-[10px] sm:text-sm text-gray-300">
                                {page.data.questions?.length || 0} question(s)
                            </span>
                            {isVisited && <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />}
                        </div>
                        <div className="p-3 sm:p-6 bg-gray-50">
                            {isSubmitted ? (
                                <div className="text-center py-2 sm:py-4">
                                    <div className="text-xl sm:text-2xl font-bold text-green-600 mb-1 sm:mb-2">
                                        ✅ {evalResult.score}%
                                    </div>
                                    <p className="text-xs sm:text-sm text-gray-600 font-serif">
                                        {evalResult.correctCount}/{evalResult.totalQuestions} bonnes réponses
                                    </p>
                                    <p className={`text-[10px] sm:text-sm font-semibold mt-1 sm:mt-2 ${evalResult.isPassed ? 'text-green-600' : 'text-red-600'}`}>
                                        {evalResult.isPassed ? '✅ Évaluation validée !' : '❌ Évaluation non validée'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs sm:text-sm text-gray-600 font-serif text-center py-2 sm:py-4">
                                        Cette évaluation contient {page.data.questions?.length || 0} questions.
                                    </p>
                                    <button
                                        onClick={() => {
                                            const questions = page.data.questions || [];
                                            const correctCount = Math.floor(Math.random() * (questions.length + 1));
                                            const score = Math.round((correctCount / questions.length) * 100);
                                            const isPassed = score >= 70;

                                            setEvaluationResults(prev => ({
                                                ...prev,
                                                [evalIndex]: {
                                                    score,
                                                    correctCount,
                                                    totalQuestions: questions.length,
                                                    isPassed
                                                }
                                            }));
                                            if (!visitedPages.includes(page.id)) {
                                                setVisitedPages(prev => [...prev, page.id]);
                                                pageMarkedRef.current[page.id] = true;
                                            }
                                        }}
                                        className="w-full py-2 sm:py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition font-serif font-semibold text-xs sm:text-sm"
                                    >
                                        📤 Soumettre l'évaluation
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                );
            }

            case 'MODULE_END':
                return (
                    <div className="flex flex-col items-center justify-center h-full min-h-[200px] sm:min-h-[300px] p-4 sm:p-8 border border-gray-300 rounded-lg bg-white">
                        <Award className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mb-2 sm:mb-4" />
                        <h1 className="text-xl sm:text-3xl font-bold text-center font-serif mb-1 sm:mb-2">
                            {page.data.resumeModule?.titreResume || '✅ Module terminé !'}
                        </h1>
                        <p className="text-sm sm:text-xl text-gray-600 font-serif text-center max-w-2xl">
                            {page.data.resumeModule?.aRetenir || 'Félicitations ! Vous avez terminé le module.'}
                        </p>
                        {isVisited && (
                            <div className="mt-3 sm:mt-4 text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="text-[10px] sm:text-sm font-serif">Déjà visitée</span>
                            </div>
                        )}
                    </div>
                );

            case 'FIN_FORMATION':
                return (
                    <div className="flex flex-col items-center justify-center h-full min-h-[200px] sm:min-h-[300px] p-4 sm:p-8 border border-gray-300 rounded-lg bg-white">
                        <Award className="w-16 h-16 sm:w-20 sm:h-20 text-gray-600 mb-2 sm:mb-4" />
                        <h1 className="text-2xl sm:text-4xl font-bold text-center font-serif mb-1 sm:mb-2">
                            {page.data.titreResume || 'Félicitations !'}
                        </h1>
                        <p className="text-sm sm:text-xl text-gray-600 font-serif text-center max-w-2xl">
                            {page.data.aRetenir || 'Vous avez terminé la formation avec succès.'}
                        </p>
                        {isVisited && (
                            <div className="mt-3 sm:mt-4 text-green-600 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" /> <span className="text-[10px] sm:text-sm font-serif">Déjà visitée</span>
                            </div>
                        )}
                    </div>
                );

            default:
                return (
                    <div className="border border-gray-300 rounded-lg bg-white p-4 sm:p-8 text-center">
                        <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500 mx-auto mb-2 sm:mb-4" />
                        <p className="text-sm sm:text-base text-gray-600 font-serif">
                            Type de page inconnu: {page.type}
                        </p>
                    </div>
                );
        }
    };

    // ============================================================
    // 13. Rendu principal
    // ============================================================
    if (!isOpen || !module) return null;

    if (!parsedContent || parsedContent.error) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
                <div className="bg-white p-4 sm:p-8 rounded-2xl shadow-2xl max-w-md w-full mx-2">
                    <div className="text-center">
                        <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 text-red-500 mx-auto mb-4" />
                        <h3 className="text-base sm:text-lg font-serif font-bold text-gray-800">
                            Erreur de chargement
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-2 font-serif">
                            {parsedContent?.error || 'Contenu invalide'}
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-4 px-4 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentPage = navigationPages[currentPageIndex] || navigationPages[0];
    const totalPages = navigationPages.length;
    const visitedCount = visitedPages.length;
    const progressValue = totalPages > 0 ? Math.round((visitedCount / totalPages) * 100) : 0;
    const isLastPage = currentPageIndex === totalPages - 1;
    const showTerminer = isLastPage;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div
                ref={containerRef}
                className="relative w-full max-w-5xl h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="bg-gray-700 text-white p-2 sm:p-3 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <button onClick={onClose} className="p-1.5 hover:bg-gray-600 rounded-lg transition">
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <div className="flex items-center gap-1.5 sm:gap-2">
                            <BookOpen className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-gray-300" />
                            <span className="font-serif font-bold text-[10px] sm:text-sm truncate">
                                {formationTitre || 'Formation'}
                            </span>
                            <span className="text-[8px] sm:text-xs text-gray-300 bg-gray-600 px-1.5 py-0.5 rounded-full">
                                Module {moduleOrdre}/{totalModules}
                            </span>
                        </div>
                        <div className="flex items-center gap-1 ml-1 sm:ml-2">
                            <div className="w-16 sm:w-24 h-1.5 bg-gray-600 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-400 rounded-full transition-all"
                                    style={{ width: `${progressValue}%` }}
                                />
                            </div>
                            <span className="text-[8px] sm:text-xs text-gray-300">{progressValue}%</span>
                            <span className="text-[7px] sm:text-xs text-gray-400 ml-0.5 sm:ml-1">({visitedCount}/{totalPages})</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
                        <div className="flex items-center gap-1 bg-gray-600 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-300" />
                            <span className="font-mono text-[10px] sm:text-sm font-medium">
                                {formatTime(timeSpent)}
                            </span>
                        </div>
                        <button
                            onClick={() => setIsPaused(!isPaused)}
                            className={`p-1.5 rounded-lg transition ${isPaused ? 'bg-yellow-500/50 hover:bg-yellow-500' : 'hover:bg-gray-600'}`}
                        >
                            {isPaused ? <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" /> : <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </button>
                        <button onClick={toggleFullscreen} className="p-1.5 hover:bg-gray-600 rounded-lg transition">
                            {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> : <Maximize2 className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
                        </button>
                        <button
                            onClick={handleManualSave}
                            className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-[8px] sm:text-xs bg-gray-600 hover:bg-gray-500 rounded-lg transition"
                            title="Sauvegarder la progression"
                        >
                            💾
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-100">
                    {renderPage(currentPage)}
                </div>

                {/* Footer réorganisé en deux lignes */}
                <div className="bg-gray-100 border-t border-gray-200 p-2 sm:p-3 flex flex-col gap-1 sm:gap-2 flex-shrink-0">
                    {/* Ligne 1 : Navigation page centrée et plus grande */}
                    <div className="flex items-center justify-center gap-1 sm:gap-3 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-200 mx-auto max-w-full">
                        <span className="text-xs sm:text-sm font-medium text-gray-600">Page :</span>
                        <button
                            onClick={goToHome}
                            disabled={currentPageIndex === 0}
                            className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                        >
                            <Home className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                            onClick={goToPreviousPage}
                            disabled={currentPageIndex === 0}
                            className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                        >
                            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <span className="text-sm sm:text-base font-bold text-gray-700 px-1 min-w-[50px] text-center">
                            {currentPageIndex + 1} / {totalPages}
                        </span>
                        <button
                            onClick={goToNextPage}
                            disabled={currentPageIndex === totalPages - 1}
                            className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-30 transition"
                        >
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        {showTerminer && (
                            <button
                                onClick={handleTerminer}
                                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-green-700 hover:bg-green-800 rounded-lg transition flex items-center gap-1"
                            >
                                <LogOut className="w-4 h-4" /> Terminer
                            </button>
                        )}
                    </div>

                    {/* Ligne 2 : Navigation modules, alignée à droite et plus petite */}
                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <span className="text-[8px] sm:text-xs text-gray-500 font-serif">Module {moduleOrdre}/{totalModules}</span>
                        <button
                            onClick={() => handleModuleChange(-1)}
                            disabled={moduleOrdre <= 1}
                            className="px-2 py-1 sm:px-3 sm:py-1.5 text-[8px] sm:text-xs font-serif text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 transition flex items-center gap-0.5"
                        >
                            <Layers className="w-3 h-3 sm:w-4 sm:h-4" /> Précédent
                        </button>
                        <button
                            onClick={() => handleModuleChange(1)}
                            disabled={moduleOrdre >= totalModules || !isCurrentModuleComplete}
                            className="px-2 py-1 sm:px-3 sm:py-1.5 text-[8px] sm:text-xs font-serif text-gray-700 hover:bg-gray-200 rounded disabled:opacity-30 transition flex items-center gap-0.5"
                        >
                            Suivant <Layers className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}